import React from "react";
import { Row, Col, Card, CardBody, Input } from "reactstrap";
import DateRangeSelector from "components/Common/DateRangeSelector";
import formatCurrency from "constants/formatCurrency";
import RefreshButton from "./RefreshButton";
import ResetFilterButton from "./ResetFilterButton";
import { useSearchParams } from "react-router-dom";
import AddMonthlyParkingSubscription from "./AddMonthlyParkingSubscription";

const MonthlyOrdersTableFilter = ({
    search,
    setSearch,
    setOrderPage,
    setDateRange,
    orders,
    filterOrders,
    filters,
    setFilters,
    setPendingFilters,
    tableView,
    setTableView,
    onApply,
    onRefresh
}) => {
    const [searchParams, setSearchParams] = useSearchParams();

    return (
        <>
            {/* Total Orders / Amount Found */}
            <Row className="mb-3 justify-content-center">
                <Col md={3}><AddMonthlyParkingSubscription orders={orders} reloadTime={onRefresh} /></Col>
                <Col md={3} className="text-center">
                    <h2>
                        Total Orders:{" "}
                        {
                            orders.flatMap((loc) =>
                                filterOrders(
                                    loc.orders.map((o) => ({
                                        ...o,
                                        locationName: loc.name,
                                    }))
                                )
                            ).length
                        }
                    </h2>
                </Col>
                <Col md={3} className="text-center">
                    <h2>
                        Total Revenue: $
                        {formatCurrency(
                            orders
                                .flatMap((loc) =>
                                    filterOrders(
                                        loc.orders.map((o) => ({
                                            ...o,
                                            locationName: loc.name,
                                        }))
                                    )
                                )
                                .reduce((sum, o) => sum + (o.total || 0), 0)
                        )}
                    </h2>
                </Col>
                <Col md={3}></Col>
            </Row>

            {/* Filter checkboxes */}
            <Row>
                {/* Table Type */}
                <Col md={3}>
                    <Card style={{ background: "#23282d", color: "#FFCA5C" }}>
                        <CardBody>
                            <h6 style={{ color: "white" }}>Table Type</h6>
                            {[
                                ["byLocation", "Show By Location"],
                                ["byOrder", "Show By Order"],
                                ["bySubscription", "Show By Subscription"],
                            ].map(([key, label]) => (
                                <div key={key} className="form-check">
                                    <Input
                                        type="radio"
                                        name="tableType"
                                        id={key}
                                        checked={tableView === key}
                                        onClick={() => setTableView(key)}
                                        className="me-2"
                                    />
                                    <label htmlFor={key} className="form-check-label">
                                        {label}
                                    </label>
                                </div>
                            ))}
                        </CardBody>
                    </Card>
                </Col>

                {/* Order Status */}
                <Col md={3}>
                    <Card style={{ background: "#23282d", color: "#FFCA5C" }}>
                        <CardBody>
                            <h6 style={{ color: "white" }}>Order Status</h6>
                            {[
                                ["succeed", "Show Completed / Active Orders"],
                                ["failed", "Show Failed / Cancelled Orders"],
                                ["pending", "Show Pending Orders"],
                                ["onHold", "Show On-Hold Orders"],
                            ].map(([key, label]) => (
                                <div key={key} className="form-check">
                                    <Input
                                        type="checkbox"
                                        id={key}
                                        checked={filters[key]}
                                        onClick={() => setPendingFilters(prev => ({ ...prev, [key]: !prev[key] }))}
                                        className="me-2"
                                    />
                                    <label htmlFor={key} className="form-check-label">
                                        {label}
                                    </label>
                                </div>
                            ))}
                        </CardBody>
                    </Card>
                </Col>

                {/* Payment Method */}
                <Col md={3}>
                    <Card style={{ background: "#23282d", color: "#FFCA5C" }}>
                        <CardBody>
                            <h6 style={{ color: "white" }}>Payment Method</h6>
                            {[
                                ["stripe", "Show Stripe Payments"],
                                ["cod", "Show Cash Payments"],
                                ["cheque", "Show Check Payments"],
                                ["qbo", "Show QBO Payments"],
                            ].map(([key, label]) => (
                                <div key={key} className="form-check">
                                    <Input
                                        type="checkbox"
                                        id={key}
                                        checked={filters[key]}
                                        onClick={() => setPendingFilters(prev => ({ ...prev, [key]: !prev[key] }))}
                                        className="me-2"
                                    />
                                    <label htmlFor={key} className="form-check-label">
                                        {label}
                                    </label>
                                </div>
                            ))}
                        </CardBody>
                    </Card>
                </Col>

                {/* Others */}
                <Col md={3}>
                    <Card style={{ background: "#23282d", color: "#FFCA5C" }}>
                        <CardBody>
                            <h6 style={{ color: "white" }}>Others</h6>
                            {[
                                ["showBarcode", "Show Barcodes"],
                                ["pagination", "Show Pagination"],
                            ].map(([key, label]) => (
                                <div key={key} className="form-check">
                                    <Input
                                        type="checkbox"
                                        id={key}
                                        checked={filters[key]}
                                        onClick={() => setPendingFilters(prev => ({ ...prev, [key]: !prev[key] }))}
                                        className="me-2"
                                    />
                                    <label htmlFor={key} className="form-check-label">
                                        {label}
                                    </label>
                                </div>
                            ))}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Date Range Selector */}
            <Row className="justify-content-center">
                <Col md={4}></Col>
                <Col md={4}>
                    {(() => {
                        const now = new Date();
                        const month = now.getMonth();
                        const defaultHalf = month >= 6 ? "This Year 2H" : "This Year 1H";
                        return (
                            <DateRangeSelector
                                defaultRange={defaultHalf}
                                onApply={(start, end) => {
                                    setDateRange({ start, end });
                                    setOrderPage(1);
                                }}
                            />
                        );
                    })()}
                </Col>
                <Col md={4}>
                    <button
                        type="button"
                        className="button-gradient-success-bg px-5 py-2"
                        onClick={onApply}
                    >
                        SEARCH
                    </button>
                </Col>
            </Row>

            {/* Search Input */}
            <Row className="mb-3 justify-content-center">
                <Col md={12}>
                    <Input
                        type="text"
                        placeholder="Search by name, email, phone, plate, car, location name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Col>
            </Row>

            {/* Apply Filter Button */}
            <Row className="justify-content-center mb-4">
                <Col md="auto" className="text-center">
                    <ResetFilterButton onReset={() => {
                        setSearch("");
                        let filtersUp = {
                            succeed: false,
                            failed: false,
                            pending: false,
                            onHold: false,
                            stripe: false,
                            cod: false,
                            cheque: false,
                            qbo: false,
                            showBarcode: false,
                            pagination: true,
                        }
                        setPendingFilters(filtersUp);
                        setOrderPage(1);
                        setSearchParams({ view: "byOrder" });
                        setFilters(filtersUp);
                        onApply()
                    }} />
                    <RefreshButton onRefresh={onRefresh} />
                </Col>
            </Row>
        </>
    )
};

export default MonthlyOrdersTableFilter;